import { useEffect, useRef, useState } from 'react';

/** Same cadence as the AIcss typewriter (~2 characters / 9ms). */
const TICK_MS = 9;

function prefersReducedMotion(): boolean {
  return (
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
}

/**
 * Keep steps small so SDK token dumps don't pop in as blocks, but speed up
 * when the buffer is ahead so we don't trail the model by seconds.
 */
function charsThisTick(backlog: number): number {
  if (backlog <= 2) return backlog;
  if (backlog < 80) return 2;
  if (backlog < 240) return 4;
  if (backlog < 600) return 6;
  return Math.min(backlog, Math.max(8, Math.ceil(backlog / 20)));
}

export function advanceStreamShown(shown: string, target: string): string {
  if (!target.startsWith(shown)) return target;
  if (shown.length >= target.length) return target;
  return target.slice(
    0,
    shown.length + charsThisTick(target.length - shown.length),
  );
}

/**
 * Reveals `target` a few characters at a time so streamed tokens feel
 * continuous instead of arriving as dumps. Does not restart when `target`
 * grows as a prefix of what is already shown.
 */
export function useCatchUpText(target: string): string {
  const [shown, setShown] = useState('');
  const shownRef = useRef('');

  useEffect(() => {
    if (prefersReducedMotion()) {
      shownRef.current = target;
      setShown(target);
      return;
    }

    const start = target.startsWith(shownRef.current) ? shownRef.current : '';
    const first = advanceStreamShown(start, target);
    shownRef.current = first;
    setShown(first);
    if (first === target) return;

    const id = window.setInterval(() => {
      const next = advanceStreamShown(shownRef.current, target);
      shownRef.current = next;
      setShown(next);
      if (next === target) {
        window.clearInterval(id);
      }
    }, TICK_MS);

    return () => window.clearInterval(id);
  }, [target]);

  return shown;
}
